import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { ANIME_WIKI_TITLES } from './utils/imageSearch';
import { fetchFirstUsableImage } from './utils/imageSearch';
import { fetchMultipleImages } from './utils/imageSearch';
import { THEME_EXTRAS } from './config/theme/themeExtras';
import { useProfileStore } from './store/profileStore';
import { useThemeStore } from './store/themeStore';

function App() {
    const { selectedTheme, compColor, rotateBgImage } = useThemeStore();
    const bgImage = selectedTheme.bgImage;
    const activeProfile = useProfileStore((state) => {
        const id = state.activeProfileId;
        return id ? state.profiles.find((p) => p.id === id) : undefined;
    });
    const syncedRef = useRef(false);

    // On first mount, if there's an active profile, apply its theme.
    useEffect(() => {
        if (syncedRef.current || !activeProfile) return;
        syncedRef.current = true;
        useThemeStore.getState().setTheme(activeProfile.themeId);
        useProfileStore.getState().setActiveProfile(activeProfile.id);
    }, [activeProfile]);

    // On any theme change, if it's an anime theme without a bg image yet,
    // trigger the setTheme() which will call maybeFetchBgImage.
    useEffect(() => {
        if (selectedTheme.id.startsWith('anime-') && !selectedTheme.bgImage && selectedTheme.bgQuery) {
            import('./store/themeStore').then(({ useThemeStore }) => {
                useThemeStore.getState().setTheme(selectedTheme.id);
            });
        }
    // eslint-disable-next-line
    }, [selectedTheme.id]);

    // Pre-fetch ALL images for all anime themes on boot (multiple per theme)
    // so the 5-min rotation doesn't show blanks.
    const prefetchedRef = useRef(false);
    useEffect(() => {
        if (prefetchedRef.current) return;
        prefetchedRef.current = true;

        // Single image for themes without THEME_EXTRAS
        Object.entries(ANIME_WIKI_TITLES).forEach(([themeId, wikiTitle]) => {
            if (THEME_EXTRAS[themeId]) return; // handled below with multi-fetch
            const theme = useThemeStore.getState().themes.find((t) => t.id === themeId);
            if (theme?.bgImage) return;
            fetchFirstUsableImage(wikiTitle)
                .then((url) => {
                    if (url) useThemeStore.getState().updateTheme(themeId, { bgImage: url });
                })
                .catch(() => undefined);
        });

        // Multiple images for themes with THEME_EXTRAS
        Object.entries(THEME_EXTRAS).forEach(([themeId, extras]) => {
            const theme = useThemeStore.getState().themes.find((t) => t.id === themeId);
            if (theme?.bgImages && theme.bgImages.length > 2) return; // already fetched
            fetchMultipleImages(extras.bgQueries)
                .then((urls) => {
                    if (urls.length > 0) {
                        useThemeStore.getState().updateTheme(themeId, {
                            bgImage: urls[0],
                            bgImages: urls,
                            bgImageIndex: 0,
                        });
                    }
                })
                .catch(() => undefined);
        });
    // eslint-disable-next-line
    }, []);

    // Auto-rotate background images every 5 minutes
    useEffect(() => {
        const interval = setInterval(() => {
            rotateBgImage();
        }, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [rotateBgImage]);

    useEffect(() => {
        document.body.style.backgroundColor = selectedTheme.color.main;
        document.body.style.color = compColor;

        return () => {
            document.body.style.backgroundColor = '';
            document.body.style.color = '';
        };
    }, [selectedTheme, compColor]);

    document.addEventListener(
        'touchmove',
        (e) => {
            if (e.target === document.body) {
                e.preventDefault();
            }
        },
        { passive: false }
    );

    return (
        <div className="relative select-none">
            {bgImage && (
                <div
                    className="pointer-events-none fixed inset-0 -z-10 bg-cover bg-center"
                    style={{ backgroundImage: `url("${bgImage}")` }}
                    aria-hidden
                />
            )}
            {bgImage && (
                <div
                    className="pointer-events-none fixed inset-0 -z-10"
                    style={{ backgroundColor: `${selectedTheme.color.main}cc` }}
                    aria-hidden
                />
            )}
            <Outlet />
        </div>
    );
}

export default App;
